'use client';

import { useEffect, useRef, useState } from 'react';

import type { IntentSuggestionDto } from '@/lib/engage/suggestions/schema';

const DEBOUNCE_MS = 200;
/** Ghost completion needs a typed prefix; empty input stays blank (Quick Replies cover empty state). */
const MIN_CHARS = 1;

export type UseEngageSuggestParams = {
  enabled: boolean;
  apiOrigin: string;
  tenantId: string;
  siteId: string;
  partial: string;
  pageUrl?: string;
};

export function useEngageSuggest(params: UseEngageSuggestParams): {
  suggestions: IntentSuggestionDto[];
  loading: boolean;
} {
  const { enabled, apiOrigin, tenantId, siteId, partial, pageUrl } = params;
  const [suggestions, setSuggestions] = useState<IntentSuggestionDto[]>([]);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    // Zero network when Chat Prompts is disabled — no sync setState;
    // callers receive [] via the early return below.
    if (!enabled) {
      abortRef.current?.abort();
      return;
    }

    if (partial.trim().length < MIN_CHARS) {
      abortRef.current?.abort();
      return;
    }

    const timer = setTimeout(() => {
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;

      const base = apiOrigin.replace(/\/$/, '');
      void (async () => {
        try {
          // setState only after async work (approved pattern).
          setLoading(true);
          const res = await fetch(`${base}/api/engage/suggest`, {
            method: 'POST',
            credentials: 'omit',
            headers: { 'Content-Type': 'application/json' },
            signal: ac.signal,
            body: JSON.stringify({
              tenant_id: tenantId,
              site_id: siteId,
              partial,
              page_url: pageUrl || undefined,
            }),
          });
          if (ac.signal.aborted) return;
          if (!res.ok) {
            setSuggestions([]);
            return;
          }
          const json = (await res.json()) as { suggestions?: IntentSuggestionDto[] };
          setSuggestions(Array.isArray(json.suggestions) ? json.suggestions : []);
        } catch (err) {
          if (err instanceof DOMException && err.name === 'AbortError') return;
          if (!ac.signal.aborted) setSuggestions([]);
        } finally {
          if (!ac.signal.aborted) setLoading(false);
        }
      })();
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      abortRef.current?.abort();
    };
  }, [enabled, apiOrigin, tenantId, siteId, partial, pageUrl]);

  if (!enabled || partial.trim().length < MIN_CHARS) {
    return { suggestions: [], loading: false };
  }

  return { suggestions, loading };
}
